import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/app/hooks/useAuth';
import { candidateApi } from '@/services/endpoints/candidate';
import type { SavedJobItem } from '@/services/api/types';

interface SavedJobsContextType {
  /** Full saved list (most-recent first), for the Profile view. */
  savedJobs: SavedJobItem[];
  /** Saved posting ids for O(1) membership checks. */
  savedIds: Set<string>;
  loading: boolean;
  isSaved: (jobPostingId: string) => boolean;
  /** Toggles saved state via POST/DELETE. Returns the new saved state. */
  toggleSave: (jobPostingId: string) => Promise<boolean>;
  removeSaved: (jobPostingId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

const SavedJobsContext = createContext<SavedJobsContextType | undefined>(undefined);

export function SavedJobsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [savedJobs, setSavedJobs] = useState<SavedJobItem[]>([]);
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setSavedJobs([]);
      setSavedIds(new Set());
      return;
    }
    setLoading(true);
    try {
      const { items } = await candidateApi.getSavedJobs();
      const sorted = [...items].sort(
        (a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime(),
      );
      setSavedJobs(sorted);
      setSavedIds(new Set(sorted.map((j) => j.jobPostingId)));
    } catch {
      // Leave the last-known set in place on transient failures.
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.id]);

  // Hydrate the saved set on login; clear it on logout.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isSaved = useCallback((jobPostingId: string) => savedIds.has(jobPostingId), [savedIds]);

  const toggleSave = useCallback(
    async (jobPostingId: string): Promise<boolean> => {
      const wasSaved = savedIds.has(jobPostingId);
      // Optimistic flip so the button responds immediately.
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.delete(jobPostingId);
        else next.add(jobPostingId);
        return next;
      });
      try {
        if (wasSaved) await candidateApi.removeSavedJob(jobPostingId);
        else await candidateApi.saveJob(jobPostingId);
        // Re-pull the authoritative list (gives full item details for Profile).
        await refresh();
        return !wasSaved;
      } catch (err) {
        // Revert the optimistic flip on failure.
        setSavedIds((prev) => {
          const next = new Set(prev);
          if (wasSaved) next.add(jobPostingId);
          else next.delete(jobPostingId);
          return next;
        });
        throw err;
      }
    },
    [savedIds, refresh],
  );

  const removeSaved = useCallback(
    async (jobPostingId: string) => {
      setSavedJobs((prev) => prev.filter((j) => j.jobPostingId !== jobPostingId));
      setSavedIds((prev) => {
        const next = new Set(prev);
        next.delete(jobPostingId);
        return next;
      });
      try {
        await candidateApi.removeSavedJob(jobPostingId);
      } finally {
        await refresh();
      }
    },
    [refresh],
  );

  const value = useMemo<SavedJobsContextType>(
    () => ({ savedJobs, savedIds, loading, isSaved, toggleSave, removeSaved, refresh }),
    [savedJobs, savedIds, loading, isSaved, toggleSave, removeSaved, refresh],
  );

  return <SavedJobsContext.Provider value={value}>{children}</SavedJobsContext.Provider>;
}

export function useSavedJobs() {
  const ctx = useContext(SavedJobsContext);
  if (ctx === undefined) {
    throw new Error('useSavedJobs must be used within a SavedJobsProvider');
  }
  return ctx;
}
